import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { JsonLd } from "~/components/seo/JsonLd";
import {
  type SalaryNationalData,
  type RelatedOccupation,
} from "~/lib/salary-seo-queries";
import { formatSalary, slugify, generateHreflangLinks } from "~/lib/seo-utils";
import {
  BundeslandTable,
  SkillLevelComparison,
  SalaryCTA,
  SalaryBreadcrumb,
  SalaryAttribution,
} from "~/components/seo/SalaryCard";
import { InternalLinks } from "~/components/seo/InternalLinks";
import { serializeForServer } from "~/lib/server-serialization";

const fetchSalaryByOccupation = createServerFn({ method: "GET" })
  .validator(z.object({ beruf: z.string() }))
  .handler(
  async ({ data }): Promise<SalaryNationalData & { related: RelatedOccupation[] }> => {
    const { getSalaryByOccupation, getRelatedOccupations } = await import("~/lib/salary-seo-queries");
    const salaryData = await getSalaryByOccupation(data.beruf);
    if (!salaryData) throw notFound();
    const related = await getRelatedOccupations(
      salaryData.occupation.escoUri,
      salaryData.occupation.occupationCode,
    );
    return serializeForServer({ ...salaryData, related });
  },
);

export const Route = createFileRoute("/gehalt/$beruf")({
  loader: ({ params }) => fetchSalaryByOccupation({ data: { beruf: params.beruf } }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const name = loaderData.occupation.occupationName;
    const median = loaderData.nationalSalaries[0]?.medianMonthlyGross;
    const year = loaderData.nationalSalaries[0]?.year ?? 2026;
    const slug = loaderData.occupation.occupationName ? slugify(loaderData.occupation.occupationName) : "";
    const description = median
      ? `${name}: Median-Gehalt ${formatSalary(median)} brutto/Monat. Vergleiche Gehälter nach Bundesland und Qualifikationsstufe.`
      : `Gehaltsinformationen für ${name} in Deutschland.`;
    return {
      meta: [
        { title: `Gehalt als ${name} in Deutschland (${year}) | Alchema` },
        { name: "description", content: description },
        { property: "og:title", content: `Gehalt als ${name} in Deutschland (${year}) | Alchema` },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `https://alchema.eu/gehalt/${slug}` },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: `Gehalt als ${name} (${year}) | Alchema` },
        { name: "twitter:description", content: description },
        { name: "robots", content: "index, follow" },
      ],
      links: generateHreflangLinks(`/gehalt/${slug}`),
    };
  },
  component: GehaltBerufPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-4xl px-6 py-20 text-center">
      <h1 className="text-2xl font-bold" style={{ color: "var(--text)" }}>
        Beruf nicht gefunden
      </h1>
      <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
        Für diesen Beruf liegen keine Gehaltsdaten vor.
      </p>
      <Link
        to="/gehalt"
        className="mt-6 inline-flex rounded-[10px] px-5 py-2.5 text-sm font-medium text-white"
        style={{ background: "var(--accent)" }}
      >
        Alle Berufe ansehen
      </Link>
    </div>
  ),
});

function GehaltBerufPage() {
  const data = Route.useLoaderData();
  const { occupation, nationalSalaries, regionalBreakdown, related } = data;
  const year = nationalSalaries[0]?.year ?? regionalBreakdown[0]?.year ?? 2026;
  const overall = nationalSalaries.find(
    (s: { skillLevel: string | null }) => !s.skillLevel || s.skillLevel === "Gesamt",
  );
  const median = overall?.medianMonthlyGross ?? nationalSalaries[0]?.medianMonthlyGross ?? 0;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Occupation",
    name: occupation.occupationName,
    description: occupation.descriptionDe ?? `Gehaltsinformationen für ${occupation.occupationName}`,
    occupationLocation: { "@type": "Country", name: "Deutschland" },
    estimatedSalary: {
      "@type": "MonetaryAmountDistribution",
      name: "Brutto-Monatsgehalt",
      currency: "EUR",
      duration: "P1M",
      median,
      percentile25: overall?.q25MonthlyGross ?? undefined,
      percentile75: overall?.q75MonthlyGross ?? undefined,
    },
  };

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={JSON.stringify(jsonLd)} />
      <section className="mx-auto max-w-4xl px-6 pb-24 pt-20">
        <SalaryBreadcrumb
          items={[
            { label: "Gehalt", href: "/gehalt" },
            { label: occupation.occupationName },
          ]}
        />

        <h1 className="mt-6 text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
          Gehalt als {occupation.occupationName} in Deutschland ({year})
        </h1>

        {occupation.descriptionDe && (
          <p className="mt-3 max-w-xl text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {occupation.descriptionDe}
          </p>
        )}

        {median > 0 && (
          <div className="mt-8 rounded-xl border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <p className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>
              Median Brutto-Monatsgehalt (Deutschland)
            </p>
            <p className="mt-1 text-3xl font-bold" style={{ color: "var(--text)" }}>
              {formatSalary(median)}
            </p>
            {overall?.q25MonthlyGross && overall?.q75MonthlyGross && (
              <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                Spanne: {formatSalary(overall.q25MonthlyGross)} – {formatSalary(overall.q75MonthlyGross)}
              </p>
            )}
          </div>
        )}

        {nationalSalaries.length > 1 && (
          <div className="mt-8">
            <h2 className="mb-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
              Gehalt nach Qualifikationsstufe
            </h2>
            <SkillLevelComparison rows={nationalSalaries} />
          </div>
        )}

        {regionalBreakdown.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
              Gehalt nach Bundesland
            </h2>
            <BundeslandTable rows={regionalBreakdown} occupationSlug={slugify(occupation.occupationName)} />
          </div>
        )}

        <div className="mt-10">
          <SalaryCTA />
        </div>

        {related.length > 0 && (
          <div className="mt-10">
            <h2 className="mb-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
              Verwandte Berufe
            </h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {related.map((r: RelatedOccupation) => (
                <Link
                  key={r.slug}
                  to="/gehalt/$beruf"
                  params={{ beruf: r.slug }}
                  className="flex items-center justify-between rounded-xl border px-4 py-3 transition hover:shadow-sm"
                  style={{ borderColor: "var(--border)", background: "var(--surface)" }}
                >
                  <span className="text-sm font-medium" style={{ color: "var(--text)" }}>
                    {r.occupationName}
                  </span>
                  <span className="text-sm font-semibold" style={{ color: "var(--accent)" }}>
                    {formatSalary(r.medianMonthlyGross)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <InternalLinks
            currentType="salary"
            occupationSlug={slugify(occupation.occupationName)}
            occupationLabel={occupation.occupationName}
            relatedSlugs={related.map((r: RelatedOccupation) => ({
              slug: r.slug,
              label: `Gehalt als ${r.occupationName}`,
            }))}
          />
        </div>

        <div className="mt-10">
          <SalaryAttribution year={year} />
        </div>
      </section>
    </div>
  );
}
